import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { get } from 'lodash';
import { snakeToTitle } from '../utils/string-conversion';

@Component({
  selector: 'crud-field',
  template: `
    <div
      class="flex flex-col mb-4 text-pretty"
      [ngClass]="{ 'break-words': breakWords }"
    >
      <div
        [class]="
          isVertical
            ? 'font-normal text-[12px] flex flex-col gap-1 items-start'
            : 'font-normal text-[12px] flex flex-row gap-2 items-start'
        "
      >
        <p
          class="mt-[1px] flex items-center gap-1"
          [ngClass]="{ 'whitespace-nowrap': whitespaceNoWrap }"
          *ngIf="label"
        >
          {{ label }}:
        </p>

        <!-- Default Array Display -->
        <div
          *ngIf="
            !isLink &&
            !isColoredBorderIcon &&
            !isColoredText &&
            valueType == 'array'
          "
          class="flex flex-col"
        >
          <span *ngFor="let val of value" class="text-[13px] font-medium">
            {{ val ?? '-' }}
          </span>
          <mat-icon
            *ngIf="tooltipText"
            svgIcon="mat_outline:info"
            [matTooltip]="tooltipText"
            [matTooltipPosition]="tooltipPosition"
            [matTooltipShowDelay]="showDelay"
            [matTooltipHideDelay]="hideDelay"
            class="text-grey-500 cursor-pointer icon-size-5"
          ></mat-icon>
        </div>

        <!-- Default Single Value -->
        <span
          class="text-[13px] font-medium"
          *ngIf="
            !isLink &&
            !isColoredBorderIcon &&
            !isColoredText &&
            valueType == 'default'
          "
        >
          {{ value ?? '-' }}
        </span>
        <mat-icon
          *ngIf="tooltipText"
          svgIcon="mat_outline:info"
          [matTooltip]="tooltipText"
          [matTooltipPosition]="tooltipPosition"
          [matTooltipShowDelay]="showDelay"
          [matTooltipHideDelay]="hideDelay"
          [matTooltipClass]="'pre-line-tooltip'"
          class="text-grey-500 cursor-pointer icon-size-5"
        ></mat-icon>
      </div>

      <!-- Link Type -->
      <div class="ml-2 text-blue-600" *ngIf="isLink">
        <a [href]="value" target="_blank">{{ value ?? '-' }}</a>
      </div>

      <!-- Colored Text Box -->
      <div
        class="ml-2 mt-2 font-inter text-[13px] font-medium leading-5 normal-nums text-blue-600 rounded border-2 flex w-20 items-center py-1 p-2 flex-shrink-1"
        [style.borderColor]="'#1E56A0'"
        [style.backgroundColor]="isColoredText ? '#EFFBFF' : ''"
        *ngIf="isColoredText"
      >
        <span class="font-bold" [style.color]="'#1E56A0'">{{
          value ?? '-'
        }}</span>
        <mat-icon
          *ngIf="tooltipText"
          svgIcon="mat_outline:info"
          [matTooltip]="tooltipText"
          [matTooltipPosition]="tooltipPosition"
          [matTooltipShowDelay]="showDelay"
          [matTooltipHideDelay]="hideDelay"
          class="text-grey-500 cursor-pointer icon-size-5"
        ></mat-icon>
      </div>

      <!-- Colored Border Icon -->
      <div
        class="ml-2 mt-2 font-inter text-base font-medium leading-5 normal-nums text-blue-600 rounded border-2 flex w-40 items-center py-1 p-1 flex-shrink-1"
        [style.borderColor]="'#1E56A0'"
        [style.backgroundColor]="isColoredBorderIcon ? '#EFFBFF' : ''"
        *ngIf="isColoredBorderIcon"
      >
        <ng-container *ngIf="value === 'Announcement'">
          <mat-icon
            class="mr-1 ml-2 flex-shrink-0"
            svgIcon="mat_outline:campaign"
            [style.fill]="'#1E56A0'"
          ></mat-icon>
          <span class="font-bold" [style.color]="'#1E56A0'">{{
            value ?? '-'
          }}</span>
        </ng-container>
        <ng-container *ngIf="value === 'Consent Form'">
          <mat-icon
            class="mr-1 ml-2 flex-shrink-0"
            svgIcon="mat_outline:description"
            [style.fill]="'#1E56A0'"
          ></mat-icon>
          <span class="font-bold" [style.color]="'#1E56A0'">{{
            value ?? '-'
          }}</span>
        </ng-container>
        <ng-container *ngIf="value === 'Newsletter'">
          <mat-icon
            class="mr-2 ml-4 flex-shrink-0"
            svgIcon="mat_outline:live_tv"
            [style.fill]="'#1E56A0'"
          ></mat-icon>
          <span class="font-bold" [style.color]="'#1E56A0'">{{
            value ?? '-'
          }}</span>
        </ng-container>
      </div>
    </div>
  `,
})
export class CrudFieldComponent implements OnChanges, OnInit {
  @Input() resource: any;
  @Input() label: string = null;
  @Input() name: string = '';
  @Input() value: any;
  @Input() valueType: string = 'default';
  @Input() isLink: boolean = false;
  @Input() isColoredBorderIcon: boolean = false;
  @Input() isColoredText: boolean = false;
  @Input() whitespaceNoWrap: boolean = true;
  @Input() breakWords: boolean = false;
  @Input() isVertical: boolean = false;
  // ✅ Tooltip Inputs
  @Input() tooltipText: string = '';
  @Input() tooltipPosition: 'above' | 'below' | 'left' | 'right' = 'above';
  @Input() showDelay: number = 200;
  @Input() hideDelay: number = 100;

  constructor() {}

  public ngOnInit(): void {
    if (!this.value) {
      this.isLink = false;
    }

    if (this.label == '') {
      this.label = snakeToTitle(this.name);
    }
  }

  public ngOnChanges(changes): void {
    if (!this.value) {
      this.value = get(this.resource, this.name);
    }
  }
}
